import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import FreeSpinMaskReelController from "./FreeSpinMaskReelController";
import { safeInit } from "../../../utilities";

const FreeSpinMaskReel = forwardRef(
  (
    {
      index,
      symbolWidth,
      symbolHeight,
      reelSpacing,
      freeSpinReelRefs,
      freeSpinOverlayReelRefs,
    },
    ref
  ) => {
    const maskContainerRef = useRef(null);
    const controllerRef = useRef(null);

    // Memoize X position
    const containerX = useMemo(
      () => reelSpacing * (index - 2),
      [reelSpacing, index]
    );

    // Initialize controller once
    useEffect(() => {
      if (maskContainerRef.current && !controllerRef.current) {
        const initialReelData = [0, 1, 2];

        safeInit(() => {
          controllerRef.current = new FreeSpinMaskReelController(
            maskContainerRef.current,
            index,
            symbolWidth,
            symbolHeight,
            initialReelData,
            freeSpinReelRefs,
            freeSpinOverlayReelRefs
          );
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle resize
    useEffect(() => {
      if (controllerRef.current) {
        controllerRef.current.resizeSymbol(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
    }, []);

    // Expose controller methods to parent
    useImperativeHandle(ref, () => ({
      revealSymbols: (skipDelays = false) =>
        controllerRef.current?.revealSymbols(skipDelays),
      revealSymbolAt: (rowIndex, onReveal, onRevealComplete) =>
        controllerRef.current?.revealSymbolAt(rowIndex, onReveal, onRevealComplete),
      coverSymbols: () => controllerRef.current?.coverSymbols(),
      coverSymbolAt: (rowIndex, onCoverComplete) =>
        controllerRef.current?.coverSymbolAt(rowIndex, onCoverComplete),
      speedUpReveal: () => controllerRef.current?.speedUpReveal(),
      getMaskReelController: () => controllerRef.current,
    }));

    return (
      <Container
        ref={maskContainerRef}
        name={`mask_reel_${index}`}
        x={containerX}
      />
    );
  }
);

export default FreeSpinMaskReel;
